import { useState, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, X,
  LogIn, BarChart2, Mountain, Newspaper, ListChecks, Lightbulb, Flag,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateSection, useDeleteSection } from '../hooks';
import type { AgendaSection, ToolType } from '../types';

// =============================================================================
// Tool icons
// =============================================================================

const TOOL_ICONS: Record<ToolType, { icon: LucideIcon; color: string }> = {
  segue:      { icon: LogIn,       color: 'text-sky-500' },
  scorecard:  { icon: BarChart2,   color: 'text-emerald-500' },
  rock_review:{ icon: Mountain,    color: 'text-violet-500' },
  headlines:  { icon: Newspaper,   color: 'text-amber-500' },
  todo_list:  { icon: ListChecks,  color: 'text-blue-500' },
  ids:        { icon: Lightbulb,   color: 'text-orange-500' },
  conclude:   { icon: Flag,        color: 'text-rose-500' },
};

interface AgendaSectionRowProps {
  section: AgendaSection;
  agendaId: string;
  onDurationChange?: (sectionId: string, seconds: number) => void;
  onDeleted?: (sectionId: string) => void;
}

export function AgendaSectionRow({ section, agendaId, onDurationChange, onDeleted }: AgendaSectionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });

  const updateSection = useUpdateSection();
  const deleteSection = useDeleteSection();

  const serverDurationMinutes = Math.round(section.duration_seconds / 60);
  const [durationInput, setDurationInput] = useState(String(serverDurationMinutes));
  const [nameInput, setNameInput] = useState(section.name);

  // Sync local state when server value changes
  useEffect(() => {
    setDurationInput(String(serverDurationMinutes));
  }, [serverDurationMinutes]);

  useEffect(() => {
    setNameInput(section.name);
  }, [section.name]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  const handleNameBlur = () => {
    if (nameInput.trim() && nameInput !== section.name) {
      updateSection.mutate({
        agendaId,
        sectionId: section.id,
        input: { name: nameInput.trim() },
      });
    } else {
      setNameInput(section.name);
    }
  };

  const handleDurationBlur = () => {
    const minutes = parseInt(durationInput, 10);
    if (!isNaN(minutes) && minutes >= 0) {
      const seconds = minutes * 60;
      onDurationChange?.(section.id, seconds);
      updateSection.mutate({
        agendaId,
        sectionId: section.id,
        input: { duration_seconds: seconds },
      });
    } else {
      setDurationInput(String(serverDurationMinutes));
    }
  };

  const handleDelete = () => {
    onDeleted?.(section.id); // optimistic: remove row immediately
    deleteSection.mutate({ agendaId, sectionId: section.id });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-4 py-3 bg-background border-b last:border-b-0 hover:bg-muted/30 transition-colors"
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Tool icon (only for tool sections) */}
      {section.tool_type && TOOL_ICONS[section.tool_type] && (() => {
        const { icon: Icon, color } = TOOL_ICONS[section.tool_type!];
        return <Icon className={`h-4 w-4 shrink-0 ${color}`} />;
      })()}

      {/* Section Name */}
      <Input
        value={nameInput}
        onChange={(e) => setNameInput(e.target.value)}
        onBlur={handleNameBlur}
        className="flex-1 h-8 text-sm font-medium border-transparent hover:border-input focus:border-input bg-transparent"
        placeholder="Section name"
        aria-label="Section name"
        disabled={section.is_locked}
      />

      {/* Duration (minutes) */}
      <div className="flex items-center gap-1 shrink-0">
        <Input
          type="number"
          value={durationInput}
          onChange={(e) => setDurationInput(e.target.value)}
          onBlur={handleDurationBlur}
          className="w-16 h-8 text-sm text-center"
          min={0}
          max={240}
          aria-label="Duration in minutes"
        />
        <span className="text-xs text-muted-foreground">min</span>
      </div>

      {/* Delete Button (hidden if locked) */}
      {!section.is_locked ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={deleteSection.isPending}
          className="shrink-0 h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          aria-label={`Delete section "${section.name}"`}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : (
        <div className="shrink-0 h-8 w-8" />
      )}
    </div>
  );
}
